import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { Item } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/currency';

interface DeleteItemDialogProps {
  open: boolean;
  item: Item | null;
  onClose: () => void;
  onConfirm: (item: Item) => Promise<void>;
  isLoading?: boolean;
}

const DeleteItemDialog: React.FC<DeleteItemDialogProps> = ({
  open,
  item,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  
  const handleConfirm = async () => {
    if (item) {
      await onConfirm(item);
      onClose();
    }
  };

  if (!item) return null;

  return (
    <AppDialog presentation="confirm"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>{t('items.deleteItem')}</DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('items.deleteWarning')}
        </Alert>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <InventoryIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h6" component="div">
              {item.name}
            </Typography>
            <Typography variant="body2" color="success.main">
              {item.priceGross != null ? formatCurrency(item.priceGross) : formatCurrency(item.price)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary">
          {t('items.deleteQuestion')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          color="inherit"
        >
          {t('common.cancel')}
        </Button>
        <Button
          data-testid="item-delete-confirm"
          onClick={handleConfirm}
          disabled={isLoading}
          color="error"
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('items.deleting') : t('items.deleteItem')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
};

export default DeleteItemDialog;
